import React, { useCallback, useRef, useState } from 'react';

import type { UploadAsLatestVersionChoice } from './UploadAsLatestVersionDialog';
import { UploadAsLatestVersionDialog } from './UploadAsLatestVersionDialog';

type PendingConfirm = {
  resolve: (choice: UploadAsLatestVersionChoice) => void;
  session: number;
};

export function useUploadAsLatestVersion(): {
  confirmUploadAsLatestVersion: (documentName: string) => Promise<UploadAsLatestVersionChoice>;
  dialog: JSX.Element;
} {
  const [dialogState, setDialogState] = useState<{
    documentName: string;
    session: number;
  } | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  const sessionRef = useRef(0);

  const settle = useCallback((choice: UploadAsLatestVersionChoice, session: number) => {
    const pending = pendingRef.current;
    if (!pending || pending.session !== session) return;
    pendingRef.current = null;
    setDialogState(null);
    pending.resolve(choice);
  }, []);

  const confirmUploadAsLatestVersion = useCallback((name: string) => {
    const previous = pendingRef.current;
    if (previous) {
      pendingRef.current = null;
      setDialogState(null);
      previous.resolve('asNewDocument');
    }

    const session = ++sessionRef.current;
    return new Promise<UploadAsLatestVersionChoice>(resolve => {
      pendingRef.current = { resolve, session };
      setDialogState({ documentName: name, session });
    });
  }, []);

  const dialog = (
    <UploadAsLatestVersionDialog
      documentName={dialogState?.documentName ?? ''}
      open={dialogState !== null}
      session={dialogState?.session ?? 0}
      onChoose={settle}
    />
  );

  return { confirmUploadAsLatestVersion, dialog };
}
